import { useEffect, useState } from "react";
import { Menu, FileDown, Linkedin, Github, Mail, Zap, Microscope } from "lucide-react";
import { siteConfig } from "../config";
import { useView } from "../context/ViewContext";
import { useSafeLink, slug } from "./common";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";

const LINKS = [
  { label: "Overview", id: "overview" },
  { label: "Projects", id: "projects" },
  { label: "RCA Lab", id: "rca-lab" },
  { label: "Continuous Improvement", id: "continuous-improvement" },
  { label: "QMS", id: "qms" },
  { label: "Automation", id: "automation" },
  { label: "IoT", id: "iot" },
  { label: "Analytics Lab", id: "analytics-lab" },
  { label: "Expertise", id: "expertise" },
  { label: "About", id: "about" },
];

export default function Nav() {
  const { mode, switchMode, goTo } = useView();
  const safeLink = useSafeLink();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open && document.body.style.pointerEvents === "none") {
      document.body.style.pointerEvents = "";
    }
  }, [open]);
  useEffect(() => {
    return () => {
      document.body.style.pointerEvents = "";
    };
  }, []);

  const nav = (id) => {
    setOpen(false);
    goTo(id);
  };

  return (
    <header className="sticky top-0 z-50 bg-navy/85 backdrop-blur-md border-b border-cyan/20 print:hidden" data-testid="main-nav">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          <button onClick={() => nav("overview")} className="flex items-center gap-3 group" data-testid="nav-logo">
            <span className="w-9 h-9 rounded-md bg-gradient-to-br from-cyan/30 to-teal/25 border border-cyan/50 flex items-center justify-center font-mono text-cyan text-sm font-semibold shadow-sm shadow-cyan/20 transition-transform group-hover:scale-105">
              {siteConfig.initials}
            </span>
            <span className="hidden sm:block text-left">
              <span className="block font-sans text-[15px] font-semibold text-paper leading-tight">{siteConfig.name}</span>
              <span className="block font-mono text-[9px] uppercase tracking-[0.18em] text-cyan/75 mt-0.5">Product Quality Systems</span>
            </span>
          </button>

          <nav className="hidden xl:flex items-center gap-1" aria-label="Primary">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${slug(l.label)}`}
                onClick={() => nav(l.id)}
                className="relative px-3 py-1.5 text-xs font-body text-pale/80 hover:text-cyan transition-colors after:absolute after:bottom-0 after:left-3 after:right-3 after:h-0.5 after:bg-gradient-to-r after:from-cyan after:to-teal after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:origin-left"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center border border-teal/40 rounded-sm overflow-hidden" data-testid="view-mode-toggle">
              <button
                data-testid="recruiter-view-button"
                onClick={() => switchMode("recruiter")}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-mono transition-colors ${
                  mode === "recruiter" ? "bg-cyan text-navy" : "text-pale/70 hover:text-paper"
                }`}
              >
                <Zap className="w-3 h-3" aria-hidden="true" /> Recruiter View
              </button>
              <button
                data-testid="technical-view-button"
                onClick={() => switchMode("technical")}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-mono transition-colors ${
                  mode === "technical" ? "bg-cyan text-navy" : "text-pale/70 hover:text-paper"
                }`}
              >
                <Microscope className="w-3 h-3" aria-hidden="true" /> Technical View
              </button>
            </div>

            <div className="hidden xl:flex items-center gap-1">
              <a
                href={siteConfig.resumeUrl}
                download={siteConfig.resumeDownloadFilename}
                data-testid="nav-resume-button"
                aria-label="Download resume"
                className="inline-flex items-center justify-center h-8 px-2 text-pale/70 hover:text-cyan transition-colors"
              >
                <FileDown className="w-3.5 h-3.5" />
              </a>
              <a
                href={siteConfig.linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-linkedin-button"
                aria-label="LinkedIn profile"
                className="inline-flex items-center justify-center h-8 px-2 text-pale/70 hover:text-cyan transition-colors"
              >
                <Linkedin className="w-3.5 h-3.5" />
              </a>
              {siteConfig.githubEnabled && (
                <a
                  href={siteConfig.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="nav-github-button"
                  aria-label="GitHub profile"
                  className="inline-flex items-center justify-center h-8 px-2 text-pale/70 hover:text-cyan transition-colors"
                >
                  <Github className="w-3.5 h-3.5" />
                </a>
              )}
              <Button size="sm" data-testid="nav-contact-button" onClick={() => nav("contact")} className="bg-teal hover:bg-cyan hover:text-navy text-paper h-8 text-xs ml-1">
                <Mail className="w-3.5 h-3.5 mr-1.5" /> Contact
              </Button>
            </div>

            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" className="xl:hidden text-pale" data-testid="mobile-menu-button" aria-label="Open menu">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-navy2 border-cyan/20 w-72" aria-describedby={undefined}>
                <SheetTitle className="font-sans text-paper text-left">Navigate</SheetTitle>
                <div className="flex border border-teal/40 rounded-sm overflow-hidden mt-4">
                  <button
                    data-testid="mobile-recruiter-view-button"
                    onClick={() => { switchMode("recruiter"); setOpen(false); }}
                    className={`flex-1 px-2 py-2 text-[11px] font-mono ${mode === "recruiter" ? "bg-cyan text-navy" : "text-pale/70"}`}
                  >
                    Recruiter
                  </button>
                  <button
                    data-testid="mobile-technical-view-button"
                    onClick={() => { switchMode("technical"); setOpen(false); }}
                    className={`flex-1 px-2 py-2 text-[11px] font-mono ${mode === "technical" ? "bg-cyan text-navy" : "text-pale/70"}`}
                  >
                    Technical
                  </button>
                </div>
                <nav className="mt-4 flex flex-col" aria-label="Mobile">
                  {LINKS.map((l) => (
                    <button
                      key={l.id}
                      data-testid={`mobile-nav-link-${slug(l.label)}`}
                      onClick={() => nav(l.id)}
                      className="text-left px-2 py-2.5 text-sm text-pale/80 hover:text-cyan border-b border-cyan/10"
                    >
                      {l.label}
                    </button>
                  ))}
                  <button
                    data-testid="mobile-nav-contact"
                    onClick={() => nav("contact")}
                    className="text-left px-2 py-2.5 text-sm text-cyan border-b border-cyan/10"
                  >
                    Contact
                  </button>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
      <div aria-hidden="true" className="h-px bg-gradient-to-r from-transparent via-cyan/40 to-transparent" />
    </header>
  );
}
